import * as React from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';

import { cn } from '@/utils';
import { CircleUserRound } from 'lucide-react';

interface AvatarLeaderboardProps extends React.ComponentPropsWithoutRef<
	typeof AvatarPrimitive.Root
> {
	hasGlowBorder?: boolean;
	size?: number;
}

const AvatarLeaderboard = React.forwardRef<
	React.ComponentRef<typeof AvatarPrimitive.Root>,
	AvatarLeaderboardProps
>(({ className, hasGlowBorder = false, size = 48, children, ...props }, ref) => {
	// Original SVG: 72x72 viewBox with inner rect 52x52 (10px padding on each side)
	// Scale proportionally: padding = size * (10/52), svgSize = size + 2 * padding
	const padding = size * (10 / 52);
	const svgSize = size + 2 * padding;

	return (
		<div className="relative inline-block">
			<AvatarPrimitive.Root
				ref={ref}
				className={cn('relative flex shrink-0 overflow-hidden rounded-full', className)}
				style={{ width: `${size}px`, height: `${size}px` }}
				{...props}
			>
				{children}
			</AvatarPrimitive.Root>
			{hasGlowBorder && (
				<svg
					width={svgSize}
					height={svgSize}
					viewBox="0 0 72 72"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
				>
					<g filter="url(#filter0_d_3941_4523)">
						<rect
							x="10"
							y="10"
							width="52"
							height="52"
							rx="26"
							stroke="url(#paint0_linear_3941_4523)"
							strokeWidth="4"
							shapeRendering="crispEdges"
						/>
					</g>
					{/* Decorative chain links */}
					<g>
						<path
							d="M61.2422 34.6135V34.2383C61.2422 33.2022 62.082 32.3624 63.118 32.3624C64.154 32.3624 64.9938 33.2022 64.9938 34.2383V34.9886C64.9938 36.0247 64.154 36.8645 63.118 36.8645M64.9938 37.6149V37.9901C64.9938 39.0261 64.154 39.866 63.118 39.866C62.082 39.866 61.2422 39.0261 61.2422 37.9901V37.2397C61.2422 36.2037 62.082 35.3638 63.118 35.3638"
							stroke="#F7F7F8"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M62.5442 36.113C61.9874 36.113 61.4176 37.8013 61.9848 38.9268"
							stroke="#4B4B4E"
							strokeWidth="0.5"
							strokeLinecap="round"
						/>
						<path
							d="M61.8922 49.2379L62.0795 48.9127C62.5965 48.015 62.2879 46.868 61.3901 46.3509C60.4924 45.8339 59.3455 46.1425 58.8285 47.0403L58.454 47.6905C57.937 48.5883 58.2456 49.7353 59.1433 50.2523M57.1434 49.9663L56.9561 50.2915C56.4391 51.1892 56.7477 52.3362 57.6455 52.8533C58.5432 53.3703 59.6901 53.0617 60.2071 52.1639L60.5816 51.5137C61.0986 50.6159 60.79 49.4689 59.8923 48.9519"
							stroke="#F7F7F8"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M60.0129 49.885C60.4954 50.1629 60.1466 51.9103 59.0934 52.6026"
							stroke="#4B4B4E"
							strokeWidth="0.5"
							strokeLinecap="round"
						/>
						<path
							d="M51.8583 60.3797L52.1827 60.1913C53.0785 59.6709 53.3829 58.5228 52.8625 57.627C52.3422 56.7311 51.1942 56.4267 50.2984 56.9471L49.6495 57.3239C48.7537 57.8443 48.4493 58.9924 48.9697 59.8883M47.3787 58.643L47.0543 58.8315C46.1585 59.3518 45.8541 60.4999 46.3744 61.3958C46.8948 62.2917 48.0428 62.5961 48.9386 62.0757L49.5874 61.6988C50.4832 61.1785 50.7876 60.0304 50.2673 59.1345"
							stroke="#F7F7F8"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M49.9013 60.0035C50.181 60.485 49.0073 61.8257 47.7492 61.9005"
							stroke="#4B4B4E"
							strokeWidth="0.5"
							strokeLinecap="round"
						/>
						<path
							d="M37.4912 65.0119L37.8664 65.0133C38.9024 65.0173 39.7454 64.1807 39.7494 63.1447C39.7534 62.1086 38.9169 61.2655 37.8809 61.2615L37.1306 61.2586C36.0946 61.2546 35.2515 62.0913 35.2475 63.1273M34.5045 61.2485L34.1293 61.2471C33.0934 61.2431 32.2503 62.0797 32.2463 63.1157C32.2423 64.1517 33.0789 64.9948 34.1148 64.9988L34.8652 65.0017C35.9011 65.0057 36.7442 64.1691 36.7482 63.1331"
							stroke="#F7F7F8"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M36.0042 63.6981C36.0021 64.255 34.3117 64.8182 33.1884 64.2467"
							stroke="#4B4B4E"
							strokeWidth="0.5"
							strokeLinecap="round"
						/>
						<path
							d="M22.8661 61.8421L23.1905 62.0305C24.0863 62.5509 25.2343 62.2465 25.7547 61.3506C26.275 60.4547 25.9706 59.3066 25.0748 58.7863L24.426 58.4094C23.5302 57.889 22.3821 58.1934 21.8618 59.0893M22.1551 57.0903L21.8307 56.9019C20.9349 56.3815 19.7869 56.6859 19.2666 57.5818C18.7462 58.4776 19.0506 59.6257 19.9464 60.1461L20.5952 60.523C21.4911 61.0433 22.6391 60.7389 23.1594 59.8431"
							stroke="#F7F7F8"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M22.2304 59.958C21.9507 60.4395 20.2047 60.0842 19.5164 59.0284"
							stroke="#4B4B4E"
							strokeWidth="0.5"
							strokeLinecap="round"
						/>
						<path
							d="M11.7828 51.7925L11.9701 52.1176C12.4871 53.0154 13.634 53.3241 14.5317 52.807C15.4295 52.2899 15.7381 51.143 15.221 50.2452L14.8466 49.595C14.3295 48.6972 13.1827 48.3885 12.2849 48.9056M13.5359 47.3191L13.3487 46.994C12.8317 46.0963 11.6848 45.7876 10.787 46.3047C9.88932 46.8217 9.5807 47.9687 10.0977 48.8665L10.4722 49.5167C10.9892 50.4145 12.1361 50.7231 13.0339 50.2061"
							stroke="#F7F7F8"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M12.1628 49.8399C11.6803 50.1178 10.344 48.9391 10.2738 47.6807"
							stroke="#4B4B4E"
							strokeWidth="0.5"
							strokeLinecap="round"
						/>
						<path
							d="M7.19707 37.3877L7.19417 37.7629C7.18617 38.7989 8.01949 39.6452 9.05543 39.6532C10.0914 39.6612 10.9377 38.8279 10.9457 37.7918L10.9514 37.0415C10.9594 36.0055 10.1261 35.1592 9.09019 35.1512M10.9717 34.4153L10.9746 34.0401C10.9826 33.0041 10.1493 32.1578 9.11336 32.1498C8.07741 32.1418 7.23113 32.9752 7.22313 34.0112L7.21734 34.7615C7.20934 35.7975 8.04266 36.6439 9.0786 36.6519"
							stroke="#F7F7F8"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M8.51462 35.8961C7.95786 35.8918 7.40116 34.1992 7.97701 33.078"
							stroke="#4B4B4E"
							strokeWidth="0.5"
							strokeLinecap="round"
						/>
						<path
							d="M10.3799 22.8555L10.1903 23.1793C9.66668 24.0732 9.96683 25.2224 10.8607 25.7461C11.7546 26.2698 12.9038 25.9696 13.4274 25.0756L13.8066 24.4282C14.3303 23.5342 14.0301 22.385 13.1362 21.8614M15.134 22.1621L15.3236 21.8384C15.8473 20.9444 15.5471 19.7952 14.6532 19.2715C13.7593 18.7479 12.6102 19.048 12.0866 19.942L11.7073 20.5895C11.1837 21.4834 11.4838 22.6326 12.3777 23.1563"
							stroke="#F7F7F8"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M12.2709 22.222C11.7905 21.9406 12.1522 20.1958 13.2104 19.5113"
							stroke="#4B4B4E"
							strokeWidth="0.5"
							strokeLinecap="round"
						/>
						<path
							d="M24.9203 13.548L25.2461 13.3619C26.1457 12.8482 26.4585 11.7024 25.9448 10.8027C25.4311 9.903 24.2854 9.59014 23.3857 10.1039L22.7342 10.476C21.8345 10.9897 21.5217 12.1356 22.0354 13.0352M20.4537 11.7783L20.1279 11.9644C19.2283 12.4781 18.9154 13.6239 19.4291 14.5236C19.9429 15.4233 21.0886 15.7361 21.9882 15.2224L22.6398 14.8503C23.5394 14.3365 23.8523 13.1907 23.3385 12.291"
							stroke="#F7F7F8"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M22.9714 13.1572C23.2475 13.6407 22.064 14.9727 20.8054 15.0383"
							stroke="#4B4B4E"
							strokeWidth="0.5"
							strokeLinecap="round"
						/>
						<path
							d="M37.8898 10.9935L38.265 10.9979C39.3009 11.0099 40.1504 10.1798 40.1623 9.14381C40.1743 8.10785 39.3443 7.2583 38.3084 7.24631L37.5581 7.23762C36.5222 7.22562 35.6727 8.05571 35.6607 9.09168M34.9322 7.2072L34.5571 7.20286C33.5211 7.19086 32.6717 8.02095 32.6597 9.05692C32.6477 10.0929 33.4777 10.9424 34.5136 10.9544L35.2639 10.9631C36.2998 10.9751 37.1493 10.145 37.1613 9.10905"
							stroke="#F7F7F8"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M36.4165 9.67011C36.41 10.2269 34.7153 10.7771 33.5965 10.1969"
							stroke="#4B4B4E"
							strokeWidth="0.5"
							strokeLinecap="round"
						/>
						<path
							d="M50.1088 15.1832L50.4318 15.3741C51.3238 15.901 52.474 15.6051 53.0009 14.713C53.5279 13.821 53.2319 12.6707 52.34 12.1438L51.694 11.7621C50.802 11.2352 49.6518 11.5311 49.1248 12.4231M49.4329 10.4264L49.1099 10.2355C48.2179 9.70858 47.0677 10.0045 46.5408 10.8965C46.0139 11.7885 46.3098 12.9388 47.2017 13.4658L47.8478 13.8475C48.7397 14.3744 49.89 14.0785 50.4169 13.1864"
							stroke="#F7F7F8"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M49.4749 13.2943C49.1917 13.7737 47.4484 13.4056 46.7679 12.3447"
							stroke="#4B4B4E"
							strokeWidth="0.5"
							strokeLinecap="round"
						/>
						<path
							d="M57.227 22.432L57.0395 22.1071C56.5215 21.2098 56.8289 20.0625 57.7261 19.5445C58.6232 19.0265 59.7705 19.3339 60.2884 20.2312L60.6636 20.881C61.1816 21.7782 60.8742 22.9255 59.977 23.4435M61.9767 23.1554L62.1642 23.4803C62.6822 24.3776 62.3748 25.5248 61.4776 26.0429C60.5805 26.5609 59.4332 26.2535 58.9153 25.3562L58.5401 24.7064C58.0221 23.8092 58.3295 22.6619 59.2267 22.1439"
							stroke="#F7F7F8"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M59.1169 23.0772C58.6347 23.3556 58.9854 25.1026 60.0393 25.7938"
							stroke="#4B4B4E"
							strokeWidth="0.5"
							strokeLinecap="round"
						/>
					</g>
					<defs>
						<filter
							id="filter0_d_3941_4523"
							x="0"
							y="0"
							width="72"
							height="72"
							filterUnits="userSpaceOnUse"
							colorInterpolationFilters="sRGB"
						>
							<feFlood floodOpacity="0" result="BackgroundImageFix" />
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
								result="hardAlpha"
							/>
							<feOffset />
							<feGaussianBlur stdDeviation="4" />
							<feComposite in2="hardAlpha" operator="out" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0.14902 0 0 0 0 0.403922 0 0 0 0 1 0 0 0 1 0"
							/>
							<feBlend
								mode="normal"
								in2="BackgroundImageFix"
								result="effect1_dropShadow_3941_4523"
							/>
							<feBlend
								mode="normal"
								in="SourceGraphic"
								in2="effect1_dropShadow_3941_4523"
								result="shape"
							/>
						</filter>
						<linearGradient
							id="paint0_linear_3941_4523"
							x1="36"
							y1="10"
							x2="36"
							y2="62"
							gradientUnits="userSpaceOnUse"
						>
							<stop stopColor="#2667FF" />
							<stop offset="1" stopColor="#173E99" />
						</linearGradient>
					</defs>
				</svg>
			)}
		</div>
	);
});
AvatarLeaderboard.displayName = AvatarPrimitive.Root.displayName;

const AvatarImageLeaderboard = React.forwardRef<
	React.ComponentRef<typeof AvatarPrimitive.Image>,
	React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
	<AvatarPrimitive.Image
		ref={ref}
		className={cn('h-full w-full object-cover object-center', className)}
		loading="lazy"
		{...props}
	/>
));
AvatarImageLeaderboard.displayName = AvatarPrimitive.Image.displayName;

interface AvatarFallbackLeaderboardProps extends React.ComponentPropsWithoutRef<
	typeof AvatarPrimitive.Fallback
> {
	iconSize?: number;
}

const AvatarFallbackLeaderboard = React.forwardRef<
	React.ComponentRef<typeof AvatarPrimitive.Fallback>,
	AvatarFallbackLeaderboardProps
>(({ className, iconSize = 32, ...props }, ref) => (
	<AvatarPrimitive.Fallback
		ref={ref}
		className={cn(
			'flex h-full w-full items-center justify-center rounded-full',
			'bg-gradient-to-br from-[#2667FD] to-[#9F63E9]',
			'text-white',
			className
		)}
		{...props}
	>
		<CircleUserRound strokeWidth={1} size={iconSize} />
	</AvatarPrimitive.Fallback>
));
AvatarFallbackLeaderboard.displayName = AvatarPrimitive.Fallback.displayName;

export { AvatarLeaderboard, AvatarImageLeaderboard, AvatarFallbackLeaderboard };
